import { Duration } from 'js-joda';
import { TockCookieName } from '../constants/browser-storage/TockCookies';
import { core } from '@tock/coretypes';
import { getCookieForDomain, seCookieForDomain } from './authCookieUtils';

const REFRESH_THRESHOLD_MINUTES = 1; // Threshold to refresh CSRF token before it expires

/**
 * Calculates a safe interval (in ms) for refreshing the CSRF token.
 * The interval is 80% of the token's expiration, but never more than expiration - 1 minute, and at least 1 minute.
 */
export function calculateCsrfIntervalMs(expiresMinutes: number): number {
  const intervalMins = Math.floor(expiresMinutes * 0.8);
  const safeIntervalMins = Math.max(
    1,
    Math.min(intervalMins, expiresMinutes - REFRESH_THRESHOLD_MINUTES)
  );
  return Duration.ofMinutes(safeIntervalMins).toMillis();
}

/**
 * Reports a CSRF-related error to Rollbar if available.
 */
export function reportCsrfError(message: string): void {
  if (typeof window !== 'undefined' && window.LOGGER) {
    window.LOGGER?.error(message);
  }
}

/**
 * Clears a CSRF interval timer and sets the ref to null.
 */
export function clearCsrfInterval(ref: { current: NodeJS.Timeout | null }): void {
  if (ref.current) {
    clearInterval(ref.current);
    ref.current = null;
  }
}

/**
 * Main CSRF setup logic. Handles refreshing the CSRF token and setting up a refresh interval.
 *
 * @param getCsrfToken - async function to refresh the CSRF token, must return an object with payload.expiresMinutes
 * @param hasCsrfProtection - boolean indicating if CSRF protection is enabled
 * @param intervalRef - ref object to store the interval (either { current } or { csrfInterval })
 */
export async function setupCsrfTokenRefresh(
  getCsrfToken: () => Promise<{ payload: core.CsrfToken }>,
  hasCsrfProtection: boolean,
  shouldSetCookie: boolean,
  intervalRef: { current: NodeJS.Timeout | null }
): Promise<void> {
  if (!hasCsrfProtection) {
    return;
  }

  try {
    const resp = await getCsrfToken();
    const csrfData = resp.payload;

    if (csrfData.expiresMinutes < 1) {
      reportCsrfError(
        `CSRF token csrfData.expiresMinutes should be longer than 1 minute: ${csrfData.expiresMinutes}`
      );
    }

    const intervalMs = calculateCsrfIntervalMs(csrfData.expiresMinutes);

    // Set a cookie so we can restore the token across fast refreshes
    // (looking at you, Consumer!)
    if (shouldSetCookie) {
      // This is safe as long as the backend is validating the csrf token
      // from the request header and not the cookie.
      setCsrfCookie(csrfData, intervalMs);
    }

    clearCsrfInterval(intervalRef);

    intervalRef.current = setInterval(() => {
      getCsrfToken();
    }, intervalMs);
  } catch (error) {
    reportCsrfError(`Failed to refresh CSRF token: ${error}`);
  }
}

/**
 * This is used to help convert ICsrfToken interface into a more
 * consistent CsrfToken interface by verifying all fields are present.
 */
export function verifyCsrfToken(
  csrfToken: core.ICsrfToken | null | undefined
): core.CsrfToken | null {
  if (!csrfToken) {
    return null;
  }
  const { type, patronId, expiresMinutes, token } = csrfToken;
  if (type == null || patronId == null || expiresMinutes == null || token == null) {
    return null;
  }
  return { type, patronId, expiresMinutes, token };
}

/**
 * Sets the CSRF cookie for the specified domain.
 * If overrideDurationMs is provided, it will be used as the cookie duration instead of calculating from expiresMinutes.
 */
export function setCsrfCookie(csrfData: core.CsrfToken, overrideDurationMs: number | null) {
  if (!window?.location || csrfData == null) {
    return;
  }

  const domainName = window?.location?.hostname;
  const intervalMs =
    overrideDurationMs === null
      ? calculateCsrfIntervalMs(csrfData.expiresMinutes)
      : overrideDurationMs;

  seCookieForDomain(TockCookieName.CSRF, domainName, csrfData.token, intervalMs);
}

/**
 * Retrieves the CSRF cookie for the specified domain.
 */
export function getCsrfCookie(domain: string) {
  return getCookieForDomain(TockCookieName.CSRF, domain);
}
